import { SuggestedRepliesService } from "client/openapi/services/SuggestedRepliesService";
import { COMPACT_TEXTAREA } from "components/Inputs/compact";
import { useEffect, useState } from "react";
import { SECTION, SECTION_TEXT, SECTION_TITLE } from "./settingsStyles";

/** The longest prompt the server keeps. */
const PROMPT_CHARS = 4000;

const EXAMPLE = `For example:
We're Apollo Tutoring. We tutor math and reading for grades 3 to 12, online and at our Main Street office.
Sessions are 60 minutes. Families can reschedule with 24 hours' notice.
Keep replies warm, short and professional, and don't sign them.`;

/**
 * What Tadpole's AI is told about the business to draft suggested replies,
 * as saved and as typed, for the settings dialog's Save and Cancel. It loads
 * the first time `wanted` is true, and again after reset().
 */
export function useReplyPrompt(orgId: number, wanted: boolean) {
  const [saved, setSaved] = useState<string>();
  const [prompt, setPrompt] = useState<string>();
  const [loadFailed, setLoadFailed] = useState(false);

  const loaded = prompt !== undefined;
  useEffect(() => {
    if (!wanted || loaded) return;
    let current = true;
    SuggestedRepliesService.getReplyPrompt({ orgId })
      .then((stored) => {
        if (!current) return;
        setSaved(stored.prompt);
        setPrompt(stored.prompt);
      })
      .catch(() => current && setLoadFailed(true));
    return () => {
      current = false;
    };
  }, [orgId, wanted, loaded]);

  return {
    prompt,
    setPrompt,
    loadFailed,
    changed: prompt !== saved,
    save: async () => {
      if (prompt === undefined) return;
      await SuggestedRepliesService.setReplyPrompt({
        requestBody: { org_id: orgId, prompt },
      });
      setSaved(prompt);
    },
    /** Starts over, as if the tab had never been opened. */
    reset: () => {
      setSaved(undefined);
      setPrompt(undefined);
      setLoadFailed(false);
    },
  };
}

export type ReplyPromptForm = ReturnType<typeof useReplyPrompt>;

/**
 * Organization Settings > Messaging: settings sections stacked one under
 * another, suggested replies first.
 */
export default function MessagingSettings({
  replyPrompt,
}: {
  replyPrompt: ReplyPromptForm;
}) {
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <SuggestedReplies replyPrompt={replyPrompt} />
    </div>
  );
}

/** The prompt's box. The dialog's Save saves it. */
function SuggestedReplies({
  replyPrompt: { prompt, setPrompt, loadFailed },
}: {
  replyPrompt: ReplyPromptForm;
}) {
  return (
    <section className={SECTION}>
      <label htmlFor="reply-prompt" className={SECTION_TITLE}>
        Suggested replies
      </label>
      <p className={SECTION_TEXT}>
        When a text or email about tutoring comes in, Tadpole's AI (Claude, by
        Anthropic) reads it with what you write here and drafts a short reply.
        It shows in light gray in the Communication reply box: press Tab to use
        it, edit it if you like, then Send. Messages that aren't about tutoring,
        like sales or spam, get no suggestion.
      </p>
      <p className={SECTION_TEXT}>
        Describe your policies, how replies should sound, and how your program
        works. Leave this empty to turn suggestions off.
      </p>
      {loadFailed ? (
        <p className="mt-4 text-sm text-red-600">
          Couldn't load this. Close Organization Settings and open it again.
        </p>
      ) : prompt !== undefined ? (
        <>
          <textarea
            id="reply-prompt"
            rows={10}
            maxLength={PROMPT_CHARS}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={EXAMPLE}
            className={`${COMPACT_TEXTAREA} mt-4`}
          />
          {prompt.length > PROMPT_CHARS * 0.9 && (
            <p className="mt-1 text-right text-xs text-gray-500">
              {prompt.length.toLocaleString()} of{" "}
              {PROMPT_CHARS.toLocaleString()} characters
            </p>
          )}
        </>
      ) : (
        <div className="mt-4 h-60 animate-pulse rounded-md bg-gray-100" />
      )}
    </section>
  );
}
